import React from 'react';
import { Link } from 'react-router-dom';
import { Home as HomeIcon, Briefcase, Check, ArrowRight, Zap, Users, FileText, Wrench, Camera, BarChart3, Shield, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

export default function ForAgents() {
  const benefits = [
    {
      icon: Zap,
      title: "Efficient Portfolio Management",
      description: "Manage portfolios with lower operational burden using automated workflows and standardized processes."
    },
    {
      icon: FileText,
      title: "Standardised Onboarding",
      description: "Consistent onboarding and compliance workflows across all properties and tenants."
    },
    {
      icon: Wrench,
      title: "Faster Repairs",
      description: "Automated repair routing, contractor assignment, and clearer evidence trails."
    },
    {
      icon: Camera,
      title: "Smart Inspection Engine",
      description: "Video capture, 3D models, and intelligent condition comparison tools."
    },
    {
      icon: Users,
      title: "Contractor Network Access",
      description: "Access to vetted contractors with first-to-accept job routing."
    },
    {
      icon: BarChart3,
      title: "Analytics & Reporting",
      description: "Clear statements, audit trails, and portfolio performance metrics."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-slate-50">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="p-2 bg-primary rounded-lg">
              <HomeIcon className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-display font-extrabold text-primary">LetWise</h1>
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/landlords" className="text-slate-700 hover:text-primary transition-colors font-semibold">
              For Landlords
            </Link>
            <Link to="/tenants" className="text-slate-700 hover:text-primary transition-colors font-semibold">
              For Tenants
            </Link>
            <Link to="/agents" className="text-purple-600 font-semibold">
              For Agents
            </Link>
            <Link to="/login">
              <Button>Sign In</Button>
            </Link>
          </nav>
          <div className="flex md:hidden items-center gap-3">
            <Link to="/login" className="text-sm text-slate-700 font-medium">Sign In</Link>
            <Link to="/register?role=agent">
              <Button size="sm">Partner</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 py-16 md:py-24">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium mb-6">
            <Briefcase className="w-4 h-4" />
            For Letting Agents
          </div>
          <h1 className="text-4xl md:text-5xl font-display font-extrabold leading-tight mb-6">
            Modern tooling,
            <span className="text-purple-600 block">new revenue channels.</span>
          </h1>
          <p className="text-xl text-slate-600 mb-8 leading-relaxed">
            LetWise supports agents who want to operate more efficiently by using our platform&apos;s workflows, tenant journey, inspection tools, and contractor routing—reducing overhead while maintaining service quality.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/register?role=agent">
              <Button size="lg" className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700">
                Partner With LetWise
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Button 
              size="lg" 
              variant="outline" 
              className="w-full sm:w-auto"
              onClick={() => window.location.href = 'mailto:agents@letwise.com?subject=Agent Access Request'}
            >
              Request Agent Access
            </Button>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-display font-bold text-center mb-4">Why Partner With LetWise?</h2>
          <p className="text-lg text-slate-600 text-center mb-12 max-w-3xl mx-auto">
            We&apos;re not here to replace agents—we&apos;re here to give you the tools to operate more efficiently and profitably.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                  <div className="p-3 bg-purple-100 rounded-lg w-fit mb-4">
                    <Icon className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-bold mb-2">{benefit.title}</h3>
                  <p className="text-slate-600 text-sm">{benefit.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tech Layer Option */}
      <section className="py-16 bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-display font-bold mb-6">Use LetWise as Your Tech Layer</h2>
              <p className="text-lg opacity-90 mb-6">
                Option to use LetWise as a technology platform for your own brand. White-label capabilities coming soon.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 flex-shrink-0" />
                  <span>Standardized workflows across your portfolio</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 flex-shrink-0" />
                  <span>Automated compliance tracking and reminders</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 flex-shrink-0" />
                  <span>Access to contractor marketplace</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 flex-shrink-0" />
                  <span>Integrated tenant onboarding and vetting</span>
                </li>
              </ul>
            </div>
            <Card className="p-8 bg-white text-slate-900">
              <h3 className="font-bold mb-6 text-center text-purple-700">Agent Partnership Tiers</h3>
              <div className="space-y-4">
                <div className="p-4 border-2 border-purple-500 rounded-lg bg-purple-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold">Platform Access</span>
                    <span className="text-xs bg-purple-600 text-white px-2 py-1 rounded">Available Now</span>
                  </div>
                  <p className="text-sm text-slate-600">Full access to LetWise tools and workflows</p>
                </div>
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold">White Label</span>
                    <span className="text-xs bg-slate-200 text-slate-700 px-2 py-1 rounded">Coming Soon</span>
                  </div>
                  <p className="text-sm text-slate-600">Your brand, our technology</p>
                </div>
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold">Enterprise</span>
                    <span className="text-xs bg-slate-200 text-slate-700 px-2 py-1 rounded">Contact Us</span>
                  </div>
                  <p className="text-sm text-slate-600">Custom integrations and dedicated support</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <Card className="p-12 text-center bg-white border-2 border-purple-100">
            <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-purple-100 flex items-center justify-center">
              <Briefcase className="w-8 h-8 text-purple-600" />
            </div>
            <h2 className="text-3xl font-display font-bold mb-4">Ready to modernize your agency?</h2>
            <p className="text-lg text-slate-600 mb-8">Join our agent partnership program and transform how you operate.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register?role=agent">
                <Button size="lg" className="bg-purple-600 hover:bg-purple-700">
                  Partner With LetWise
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Button 
                size="lg" 
                variant="outline" 
                onClick={() => window.location.href = 'mailto:agents@letwise.com?subject=Agent Access Request'}
              >
                Request Agent Access
              </Button>
            </div>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-slate-400">&copy; 2025 LetWise. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
